import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import type { ChangeEvent, FC } from 'react';
import { DATEPICKER_FORMAT, ORDERS_TABLE_STATUS } from '~/constants';
import { Logger } from '~/lib/logger';
import { OrdersTableStatusSchema } from '~/types/OrdersTableData';
import { getTableStatus } from '~/utils';
import type { OrdersTableToolbarProps } from './OrdersTable.types';

const logger = new Logger('ShopifyOrdersTableToolbar');

export const OrdersTableToolbar: FC<OrdersTableToolbarProps> = ({
  status,
  setStatus,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  handleExport,
  isExportLoading,
  type
}) => {
  const handleStatusChange = (event: SelectChangeEvent<typeof status>) => {
    const eventStatus = OrdersTableStatusSchema.safeParse(event.target.value);

    if (eventStatus.success) {
      setStatus(eventStatus.data);
    } else {
      logger.error('Failed to parse status', eventStatus.error.errors);
    }
  };

  const handleStartDateChange = (date: DateTime | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: DateTime | null) => {
    setEndDate(date);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Using map to remove duplicates
  const statusFilters = Array.from(
    new Map(
      ORDERS_TABLE_STATUS.map((status) => {
        const { label, value } = getTableStatus(status);
        return [value, { label, value }];
      })
    ).values()
  );

  return (
    <Toolbar
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(4, 1fr)', md: 'repeat(10, 1fr)' },
        gap: 1.5
      }}
      disableGutters
    >
      {/* Search */}
      <FormControl
        sx={{
          gridArea: { xs: '1 / 1 / 2 / 3', md: '1 / 1 / 2 / 3' },
          minWidth: { sm: 200 },
          width: { xs: '100%', md: 'auto' }
        }}
      >
        <TextField
          id="search"
          type="search"
          label="Search"
          onChange={handleSearchChange}
          value={searchTerm}
          data-testid="orders-table-search"
        />
      </FormControl>

      {/* Status filter */}
      {!!statusFilters.length && (
        <FormControl
          sx={{
            gridArea: { xs: '1 / 3 / 2 / 5', md: '1 / 4 / 2 / 6' },
            minWidth: { sm: 200 },
            width: { xs: '100%', md: 'auto' }
          }}
        >
          <InputLabel id="order-status" sx={{ backgroundColor: 'background.default', px: 1 }}>
            Order Status
          </InputLabel>

          <Select labelId="order-status" id="order-status-select" value={status} onChange={handleStatusChange}>
            {statusFilters.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Date Range filtering */}
      <FormControl
        sx={{
          gridArea: { xs: '2 / 1 / 3 / 5', md: '1 / 6 / 2 / 10' },
          minWidth: { sm: 200 },
          width: { xs: '100%', md: 'auto' }
        }}
      >
        <Stack direction="row" spacing={1.5}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={DateTime.now()}
            format={DATEPICKER_FORMAT}
            slotProps={{ field: { clearable: true } }}
            shouldDisableDate={(date) => !!(endDate && date > endDate)}
            sx={{ flex: 1 }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            maxDate={DateTime.now()}
            format={DATEPICKER_FORMAT}
            slotProps={{ field: { clearable: true } }}
            shouldDisableDate={(date) => !!(startDate && date < startDate)}
            sx={{ flex: 1 }}
          />
        </Stack>
      </FormControl>

      {/* Export */}
      <Tooltip
        title="Select a date range to enable export"
        placement="bottom-end"
        arrow
        disableHoverListener={startDate !== null && endDate !== null}
      >
        <Box component="span" sx={{ gridArea: { xs: '3 / 1 / 4 / 5', md: '1 / 10 / 2 / 11' } }}>
          <Button
            id={`${type}_orders_table_export_button`}
            size="large"
            color="primary"
            variant="outlined"
            sx={{ width: '100%', height: 56 }}
            disabled={startDate === null || endDate === null || isExportLoading}
            onClick={handleExport}
          >
            {isExportLoading ? 'Exporting...' : 'Export'}
          </Button>
        </Box>
      </Tooltip>
    </Toolbar>
  );
};
